import React, {useState, useRef, useEffect, useCallback} from 'react';
import ReactPlayer from 'react-player';
import {Subtitles} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Modal,
  Slider,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Settings,
  SkipNext,
  SkipPrevious,
  List,
  FiberManualRecord,
  Fullscreen,
  FullscreenExit,
  VolumeUp,
  VolumeOff,
  Speed,
  ArrowBack,
} from '@mui/icons-material';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {Video} from '@app/titles/models/video';
import {useUpdatePlayerWatchTime} from '../requests/useUpdatePlayerWatchtime';
import {useGetSingleWatch} from '../requests/useGetSingleWatch';
import {WatchedType} from '../requests/types';
import {TranslateIcon} from '@common/icons/material/Translate';

// Add these interfaces
interface Subtitle {
  link: string;
  language: string;
}

interface Quality {
  language: string;
  link: string;
  size: string;
  runtime: number;
}

interface Episode {
  qualities: Record<string, Quality>;
}

interface Season {
  episodes: Record<string, Episode>;
}

interface Series {
  seasons: Record<string, Season>;
}

interface Movie {
  qualities: Record<string, Record<string, Quality>>;
  subtitles?: Record<string, Subtitle>; // Added subtitles
}

type GroupedSeriesData = Record<string, Series>;
type GroupedMovieData = Record<string, Movie>;

interface VideoPlayerProps {
  titleId: number;
  title: string;
  data: GroupedSeriesData | GroupedMovieData;
  isSeries?: boolean;
  trailerSrc?: Video;
  resumeData?: WatchedType;
  chosenPlay?: string;
  onExit: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  titleId,
  title,
  data,
  isSeries = false,
  trailerSrc,
  resumeData,
  chosenPlay,
  onExit,
}) => {
  const seriesOrMovie = Object.values(data)[0];
  const languagePriority = ['Persian Dubbed', 'Persian'];

  console.log(data);
  const trackRef = useRef<HTMLTrackElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [currentSubtitle, setCurrentSubtitle] = useState<string | null>(null);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [selectedSubtitleLanguage, setSelectedSubtitleLanguage] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const playerRef = useRef<any>(null);
  const [playing, setPlaying] = useState(false);
  const [quality, setQuality] = useState(
    localStorage.getItem('user_prefered_quality') ?? '720p',
  );
  const [subtitleLoading, setSubtitleLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showQualities, setShowQualities] = useState<boolean>(false);
  const [showSpeed, setShowSpeed] = useState<boolean>(false);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<string>('');
  const [showLanguages, setShowLanguages] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const playingRef = useRef(playing);
  const [currentEpisode, setCurrentEpisode] = useState(() => {
    if (!isSeries) return null;
    if (chosenPlay) {
      const episodeMatch = chosenPlay.match(/S(\d+)_E(\d+)/);
      if (episodeMatch) {
        return {
          season: `S${episodeMatch[1].padStart(2, '0')}`,
          episode: `E${episodeMatch[2].padStart(2, '0')}`,
        };
      }
    }
    const firstSeason =
      Object.keys((seriesOrMovie as Series)?.seasons || {})[0] || 'S01';
    const firstEpisode =
      Object.keys(
        (seriesOrMovie as Series)?.seasons?.[firstSeason]?.episodes || {},
      )[0] || 'E01';
    return {season: firstSeason, episode: firstEpisode};
  });
  const [seekAfterQualityChange, setSeekAfterQualityChange] = useState<{
    time: number;
    shouldPlay: boolean;
  } | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const resumeConfirmed = useRef(false);

  const updatePlayerWatchTime = useUpdatePlayerWatchTime();

  const getSource = useCallback(() => {
    // 1. Check for chosenPlay match first
    if (chosenPlay) {
      // For series, verify current episode/season/quality match chosenPlay's data
      if (isSeries && currentEpisode) {
        const episodeData = (seriesOrMovie as Series)?.seasons?.[
          currentEpisode.season
        ]?.episodes?.[currentEpisode.episode];

        // Check if current quality exists in episode data
        if (episodeData?.qualities?.[quality]?.link === chosenPlay) {
          return chosenPlay;
        }
      }
      // For movies, verify quality matches
      else if (
        !isSeries &&
        //@ts-expect-error
        (seriesOrMovie as Movie)?.qualities?.[quality]?.link === chosenPlay
      ) {
        return chosenPlay;
      }
    }

    // 2. Handle trailer source
    if (trailerSrc) return trailerSrc.src;

    // 3. Series episode quality handling
    if (isSeries && currentEpisode) {
      const episodeData = (seriesOrMovie as Series)?.seasons?.[
        currentEpisode.season
      ]?.episodes?.[currentEpisode.episode];

      if (!episodeData?.qualities?.[quality]?.link) {
        const newQuality = Object.keys(episodeData?.qualities || {})[0];
        if (newQuality) {
          setQuality(newQuality);
          localStorage.setItem('user_prefered_quality', newQuality);
        }
        return episodeData?.qualities?.[newQuality]?.link;
      }
      return episodeData?.qualities?.[quality]?.link;
    }

    // 4. Movie quality handling
    const movieQualities = (seriesOrMovie as Movie)?.qualities || {};
    let qualityGroup = movieQualities[quality] || {};
    if (Object.keys(qualityGroup).length === 0) {
      let newQuality = Object.keys(movieQualities)[0];
      setQuality(newQuality);
      localStorage.setItem('user_prefered_quality', newQuality);
      qualityGroup = movieQualities[newQuality] || {};
    }

    // Find best language based on priority
    const findBestLanguage = () => {
      if (!currentLanguage) {
        for (const lang of ['Persian Dubbed', 'Persian']) {
          if (qualityGroup[lang]) return lang;
        }
        return Object.keys(qualityGroup)[0];
      }
      return currentLanguage;
    };

    const bestLanguage = findBestLanguage();
    return qualityGroup[bestLanguage]?.link;
  }, [
    chosenPlay,
    seriesOrMovie,
    isSeries,
    currentEpisode,
    quality,
    currentLanguage,
    trailerSrc,
  ]);

  const getAvailableSubtitles = useCallback(() => {
    if (!isSeries && seriesOrMovie?.subtitles) {
      return Object.entries(seriesOrMovie.subtitles).map(([lang, sub]) => ({
        language: lang,
        //@ts-expect-error
        link: sub.link,
      }));
    }

    return [];
  }, [isSeries, seriesOrMovie, currentEpisode]);

  // Update your subtitle selection handler
  const handleSubtitleSelect = useCallback(
    async (sub: {language: string; link: string}) => {
      try {
        setSubtitleLoading(true);
        const response = await fetch(sub.link, {
          mode: 'cors',
        });
        if (!response.ok) throw new Error('Failed to fetch subtitle');

        const srtText = await response.text();
        const vttText = convertSrtToVtt(srtText);

        const blob = new Blob([vttText], {type: 'text/vtt'});
        const blobUrl = URL.createObjectURL(blob);

        setCurrentSubtitle(blobUrl);
        setSelectedSubtitleLanguage(sub.language);
        setTimeout(() => {
          if (trackRef.current) {
            trackRef.current.track.mode = 'showing';
          }
        }, 100);
      } catch (error) {
        console.error('Error loading subtitle:', error);
        setCurrentSubtitle(null);
        setSelectedSubtitleLanguage(null);
      } finally {
        setSubtitleLoading(false);
      }
    },
    [],
  );

  // Update playing ref when playing state changes
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  // Add this useEffect for initial hide
  useEffect(() => {
    let timeoutTime = 2800;
    if (window.innerWidth < 600) {
      timeoutTime = 2300;
    }
    if (playing) {
      timeoutRef.current = setTimeout(() => {
        if (!showQualities && !showEpisodes) {
          setShowControls(false);
        }
      }, timeoutTime);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [playing]);

  useEffect(() => {
    if (isSeries) {
      const firstSeason =
        Object.keys((seriesOrMovie as Series)?.seasons || {})[0] || 'S01';
      const firstEpisode =
        Object.keys(
          (seriesOrMovie as Series)?.seasons?.[firstSeason]?.episodes || {},
        )[0] || 'E01';
      setCurrentEpisode({season: firstSeason, episode: firstEpisode});
    }
  }, [seriesOrMovie, isSeries]);

  // Add mouse move handler
  const handleMouseMove = useCallback(() => {
    let timeoutTime = 2800;
    if (window.innerWidth < 600) {
      timeoutTime = 2300;
    }
    setShowControls(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (playingRef.current) {
        if (!showQualities && !showEpisodes) {
          setShowControls(false);
        }
      }
    }, timeoutTime); // Hide after 3 seconds of inactivity
  }, []);

  useEffect(() => {
    if (resumeData && resumeData?.watched > 0) {
      setShowResumePrompt(true);
    }
  }, [resumeData]);

  // Update handleReady to handle quality change continuation
  const handleReady = useCallback(() => {
    setIsLoading(false);
    setIsBuffering(false);

    if (seekAfterQualityChange) {
      playerRef.current!.currentTime = seekAfterQualityChange.time;
      if (seekAfterQualityChange.shouldPlay) {
        playerRef.current?.play().catch(handlePlayError);
      }
      setSeekAfterQualityChange(null);
    } else if (resumeConfirmed.current && resumeData?.watched) {
      const resumeTime = (resumeData.watched - 2) * 60;
      playerRef.current!.currentTime = resumeTime;
      setCurrentTime(resumeTime);
      playerRef.current?.play().catch(handlePlayError);
      resumeConfirmed.current = false;
    } else if (playing) {
      playerRef.current?.play().catch(handlePlayError);
    }
  }, [seekAfterQualityChange, resumeData, playing]);

  // 3. Add error handling for autoplay restrictions
  const handlePlayError = useCallback((error: any) => {
    if (error?.type === 'autoplay') {
      setPlaying(false);
      setIsLoading(false);
    }
  }, []);

  const handleBuffer = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handleBufferEnd = useCallback(() => {
    setIsBuffering(false);
  }, []);

  // Fullscreen handling
  // const toggleFullscreen = useCallback(() => {
  //   if (!containerRef.current) return;

  //   if (!document.fullscreenElement) {
  //     if (containerRef.current.requestFullscreen) {
  //       containerRef.current.requestFullscreen();
  //     } else if ((containerRef.current as any).webkitRequestFullscreen) {
  //       (containerRef.current as any).webkitRequestFullscreen();
  //     }
  //     // setIsFullscreen(true);
  //   }
  //   // else {
  //   //   if (document.exitFullscreen) {
  //   //     document.exitFullscreen();
  //   //   } else if ((document as any).webkitExitFullscreen) {
  //   //     (document as any).webkitExitFullscreen();
  //   //   }
  //   //   // setIsFullscreen(false);
  //   // }
  // }, []);

  //! THE NEW CHANGES
  // Add interval reference and latest state tracking
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const latestState = useRef({
    currentTime: 0,
    duration: 0,
    isSeries,
    currentEpisode: currentEpisode,
  });

  // Update latestState when dependencies change
  useEffect(() => {
    latestState.current = {
      currentTime,
      duration,
      isSeries,
      currentEpisode,
    };
  }, [currentTime, duration, isSeries, currentEpisode]);

  // Interval setup
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        const {currentTime, duration, isSeries, currentEpisode} =
          latestState.current;
        if (isSeries && currentEpisode) {
          updatePlayerWatchTime.mutate({
            title_id: titleId,
            duration: Math.floor(duration / 60),
            type: 'series',
            watched: Math.floor(currentTime / 60),
            season: Number(currentEpisode.season.slice(1)),
            episode: Number(currentEpisode.episode.slice(1)),
          });
        } else {
          updatePlayerWatchTime.mutate({
            title_id: titleId,
            duration: Math.floor(duration / 60),
            type: 'movie',
            watched: Math.floor(currentTime / 60),
          });
        }
      }, 60000);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [playing]);
  //! END OF NEW CHANGES

  // useEffect(() => {
  //   document.addEventListener('fullscreenchange', toggleFullscreen);

  //   return () => {
  //     document.removeEventListener('fullscreenchange', toggleFullscreen);
  //   };
  // }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [getSource()]);

  // Keyboard shortcuts
  // useEffect(() => {
  //   const handleKeyPress = (e: KeyboardEvent) => {
  //     if (!containerRef.current?.contains(document.activeElement)) return;

  //     switch (e.key.toLowerCase()) {
  //       case ' ':
  //         e.preventDefault();
  //         setPlaying(prev => !prev);
  //         break;
  //       case 'f':
  //         toggleFullscreen();
  //         break;
  //       case 'm':
  //         setMuted(prev => !prev);
  //         break;
  //       case 'arrowleft':
  //         playerRef.current?.seekTo(Math.max(0, currentTime - 5));
  //         break;
  //       case 'arrowright':
  //         playerRef.current?.seekTo(Math.min(duration, currentTime + 5));
  //         break;
  //       case 'arrowup':
  //         setVolume(prev => Math.min(1, prev + 0.1));
  //         break;
  //       case 'arrowdown':
  //         setVolume(prev => Math.max(0, prev - 0.1));
  //         break;
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyPress);
  //   return () => document.removeEventListener('keydown', handleKeyPress);
  // }, [currentTime, duration, toggleFullscreen]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && playerRef.current) {
        // Tab is visible again, force seek to current time to resync
        const currentTime = playerRef.current.currentTime;
        setCurrentTime(currentTime);
        playerRef.current.play().catch(handlePlayError);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Timeline progress
  const handleProgress = useCallback(
    ({playedSeconds}: {playedSeconds: number}) => {
      setCurrentTime(playedSeconds);
    },
    [],
  );

  const handleSeek = useCallback(
    (event: Event, newValue: number | number[]) => {
      event.stopPropagation();
      const seekTime = Array.isArray(newValue) ? newValue[0] : newValue;
      playerRef.current?.seekTo(seekTime);
      setCurrentTime(seekTime);
    },
    [],
  );

  // Click to play/pause
  const handleContainerClick = (e: React.MouseEvent) => {
    if (playerRef.current) {
      if (playerRef.current.paused) {
        playerRef.current.play().then(() => setPlaying(true));
      } else {
        playerRef.current.pause();
        setPlaying(false);
      }
    }
    setShowQualities(false);
    setShowEpisodes(false);
    setShowSpeed(false);
  };

  // Episode navigation
  const navigateEpisode = useCallback(
    (direction: 'next' | 'prev') => {
      if (!isSeries || !currentEpisode || !seriesOrMovie) return;

      // Changed from data to seriesOrMovie
      const seasons = Object.keys((seriesOrMovie as Series)?.seasons || {});
      const episodes = Object.keys(
        (seriesOrMovie as Series)?.seasons?.[currentEpisode.season]?.episodes ||
          {},
      );

      const currentSeasonIndex = seasons.indexOf(currentEpisode.season);
      const currentEpisodeIndex = episodes.indexOf(currentEpisode.episode);

      if (direction === 'next') {
        if (currentEpisodeIndex < episodes.length - 1) {
          setCurrentEpisode({
            ...currentEpisode,
            episode: episodes[currentEpisodeIndex + 1],
          });
        } else if (currentSeasonIndex < seasons.length - 1) {
          const nextSeason = seasons[currentSeasonIndex + 1];
          const nextSeasonEpisodes = Object.keys(
            (seriesOrMovie as Series)?.seasons?.[nextSeason]?.episodes || {},
          );
          setCurrentEpisode({
            season: nextSeason,
            episode: nextSeasonEpisodes[0] || 'E01',
          });
        }
      } else {
        if (currentEpisodeIndex > 0) {
          setCurrentEpisode({
            ...currentEpisode,
            episode: episodes[currentEpisodeIndex - 1],
          });
        } else if (currentSeasonIndex > 0) {
          const prevSeason = seasons[currentSeasonIndex - 1];
          const prevSeasonEpisodes = Object.keys(
            (seriesOrMovie as Series)?.seasons?.[prevSeason]?.episodes || {},
          );
          setCurrentEpisode({
            season: prevSeason,
            episode: prevSeasonEpisodes[prevSeasonEpisodes.length - 1] || 'E01',
          });
        }
      }
      setCurrentTime(0);
      setPlaying(true);
    },
    [isSeries, currentEpisode, seriesOrMovie],
  );

  const handleResumeConfirm = () => {
    if (
      resumeData?.type === 'series' &&
      resumeData.season &&
      resumeData.episode
    ) {
      setCurrentEpisode({
        season: `S${resumeData.season.length === 1 ? `0${resumeData.season}` : resumeData.season}`,
        episode: `E${resumeData.episode.length === 1 ? `0${resumeData.episode}` : resumeData.episode}`,
      });
    }

    resumeConfirmed.current = true;
    setShowResumePrompt(false);

    // If video is already loaded, seek immediately
    if (playerRef.current && playerRef.current.readyState >= 2) {
      //@ts-expect-error
      const resumeTime = (resumeData?.watched - 2) * 60 || 0;
      playerRef.current.currentTime = resumeTime;
      setCurrentTime(resumeTime);
      playerRef.current.play().catch(handlePlayError);
    }
  };

  useEffect(() => {
    if (playerRef.current && playing) {
      // Start playback when episode changes
      playerRef.current.play().catch(handlePlayError);
    }
  }, [currentEpisode]);

  // Update the changeQuality function
  const changeQuality = useCallback((newQuality: string) => {
    if (!playerRef.current) return;

    const currentTime = playerRef.current.currentTime;
    const wasPlaying = !playerRef.current.paused;

    setSeekAfterQualityChange({time: currentTime, shouldPlay: wasPlaying});
    setQuality(newQuality);
    localStorage.setItem('user_prefered_quality', newQuality);
    setShowQualities(false);
  }, []);

  const changeLanguage = useCallback(
    (newLanguage: string) => {
      if (!playerRef.current) return;

      const currentTime = playerRef.current.currentTime;
      const wasPlaying = !playerRef.current.paused;

      setSeekAfterQualityChange({time: currentTime, shouldPlay: wasPlaying});
      setCurrentLanguage(newLanguage);
      if (
        !Object.keys((seriesOrMovie as Movie).qualities[quality]).includes(
          newLanguage,
        )
      ) {
        const findtheLanguageQuality = Object.keys(
          (seriesOrMovie as Movie).qualities,
        ).find(qual =>
          Object.keys((seriesOrMovie as Movie).qualities?.[qual])?.includes(
            newLanguage,
          ),
        );
        console.log(findtheLanguageQuality);
        console.log(newLanguage);

        localStorage.setItem(
          'user_prefered_quality',
          findtheLanguageQuality as string,
        );
        setQuality(findtheLanguageQuality as string);
      }
    },
    [playing, quality],
  );

  // Add this utility function at the top of your file
  const convertSrtToVtt = (srtText: string) => {
    // Add WEBVTT header
    let vtt = 'WEBVTT\n\n';

    // Use the original text directly
    const blocks = srtText.trim().split(/\n\s*\n/);

    blocks.forEach(block => {
      const lines = block.split('\n');
      if (lines.length < 2) return;

      // Replace SRT time format (00:00:00,000) with VTT format (00:00:00.000)
      const timecodes = lines[1].replace(/,/g, '.');

      // Add timecodes and text
      vtt += `${timecodes}\n${lines.slice(2).join('\n')}\n\n`;
    });

    return vtt;
  };

  //! NEW FOR CHOSEN PLAY
  // Add this useEffect to handle chosenPlay initialization
  useEffect(() => {
    if (chosenPlay) {
      // Helper to find quality/season/episode from grouped data
      const findInData = () => {
        if (isSeries) {
          const series = Object.values(data as GroupedSeriesData)[0];
          for (const [seasonKey, season] of Object.entries(series.seasons)) {
            for (const [episodeKey, episode] of Object.entries(
              season.episodes,
            )) {
              for (const [qualityKey, quality] of Object.entries(
                episode.qualities,
              )) {
                if (quality.link === chosenPlay) {
                  return {
                    quality: qualityKey,
                    season: seasonKey,
                    episode: episodeKey,
                  };
                }
              }
            }
          }
        } else {
          const movie = Object.values(data as GroupedMovieData)[0];
          for (const [qualityKey, quality] of Object.entries(movie.qualities)) {
            //@ts-expect-error
            if (quality.link === chosenPlay) {
              return {quality: qualityKey};
            }
          }
        }
        return null;
      };

      const match = findInData();
      if (match) {
        // Set quality state
        if (match.quality && match.quality !== quality) {
          setQuality(match.quality);
          localStorage.setItem('user_prefered_quality', match.quality);
        }

        // Set episode state for series
        if (isSeries && match.season && match.episode) {
          setCurrentEpisode({
            season: match.season,
            episode: match.episode,
          });
        }
      }
    }
  }, [chosenPlay, isSeries, data]); // Run when component mounts

  useEffect(() => {
    let timeoutTime = 2800;
    if (window.innerWidth < 600) {
      timeoutTime = 2300;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (playingRef.current) {
        if (!showQualities && !showEpisodes) {
          setShowControls(false);
        }
      }
    }, timeoutTime); // Hide after 3 seconds of inactivity
  }, [showControls]);

  useEffect(() => {
    if (showEpisodes || showQualities) {
      setShowControls(true);
    }
  }, [showEpisodes, showQualities]);

  useEffect(() => {
    if (showResumePrompt) {
      setTimeout(() => {
        setShowResumePrompt(false);
      }, 20000);
    }
  }, [showResumePrompt]);

  useEffect(() => {
    if (!isSeries) {
      const movieQualities = (seriesOrMovie as Movie)?.qualities || {};
      const qualityGroup = movieQualities[quality] || {};

      // Priority order
      const langPriority = ['Persian Dubbed', 'Persian'];
      let bestLang =
        langPriority.find(lang => qualityGroup[lang]) ||
        Object.keys(qualityGroup)[0];

      if (bestLang && bestLang !== currentLanguage) {
        setCurrentLanguage(bestLang);
      }
    }
  }, [isSeries, seriesOrMovie]);

  // Add cleanup effect
  useEffect(() => {
    const current = currentSubtitle;
    return () => {
      if (current) {
        URL.revokeObjectURL(current);
      }
    };
  }, [currentSubtitle]);

  const languages = [];
  if (!isSeries) {
    for (const quality in seriesOrMovie?.qualities) {
      for (const lang in (seriesOrMovie as Movie)?.qualities?.[quality]) {
        languages.push(
          (seriesOrMovie as Movie)?.qualities[quality][lang].language,
        );
      }
    }
  }
  const uniqueLanguages = [...new Set(languages)];

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        // paddingTop: '56.25%',
        backgroundColor: '#000',
        height: '100%',
        cursor: 'pointer',
        '&:fullscreen': {
          paddingTop: 0,
          width: '100%',
        },
      }}
      // onClick={handleContainerClick}
      // onDoubleClick={toggleFullscreen}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
      onTouchStart={() => {
        setShowControls(true);
      }}
      // onClick={handleContainerClick}
    >
      {showResumePrompt && (
        <Button
          className="absolute bottom-[170px] right-6 z-[9999] rounded-lg border border-white text-white max-sm:bottom-[90px]"
          onClick={handleResumeConfirm}
          color="white"
        >
          <span className="mx-2">
            <Trans message="Continue from" />
          </span>
          {resumeData?.type === 'series' && (
            <span className="flex items-center gap-x-2">
              <span>
                <Trans message={`S${resumeData?.season}`} />
              </span>
              <span>
                <Trans message={`E${resumeData?.episode}`} />
              </span>
            </span>
          )}
          <span className="mx-2">
            {resumeData?.watched && resumeData?.watched - 2}:00
          </span>
        </Button>
      )}
      {(showControls || !playing || showQualities || showEpisodes) && (
        <Box
          sx={{
            direction: 'ltr',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          }}
          className="absolute top-0 z-[9999] flex h-80 w-full justify-center gap-y-10 pt-16"
        >
          <Button
            onClick={e => {
              e.stopPropagation();
              onExit();
            }}
          >
            <ArrowBack />
            <Trans message="Return" />
          </Button>
          {trailerSrc ? (
            <div className="mx-auto flex flex-col">
              <Trans message={trailerSrc.name} />
            </div>
          ) : (
            <div className="mx-auto flex flex-col items-center">
              <div>
                {' '}
                <Trans message={title} />
              </div>
              <div className="flex gap-x-4">
                <span>
                  <Trans message={currentEpisode?.season as string} />
                </span>
                <span>
                  <Trans message={currentEpisode?.episode as string} />
                </span>
              </div>
            </div>
          )}
        </Box>
      )}
      {/* <ReactPlayer
        ref={playerRef}
        url={getSource()}
        playing={playing}
        // playbackRate={playbackRate}
        volume={muted ? 0 : volume}
        controls={true}
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
            },
            tracks: currentSubtitle
              ? [
                  {
                    kind: 'subtitles',
                    src: currentSubtitle,
                    // Use actual language instead of hardcoded values
                    srcLang: selectedSubtitleLanguage || 'en',
                    label: selectedSubtitleLanguage || 'English',
                    default: true,
                  },
                ]
              : [],
          },
        }}
        width="100%"
        height="100%"
        style={{position: 'absolute', top: 0, left: 0}}
        onReady={handleReady}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onError={err => {
          if (err.type === 'subtitle-load-error') {
            console.error('Failed to load subtitles:', err);
            setCurrentSubtitle(null);
          } else {
            handlePlayError(err);
          }
        }}
        onProgress={handleProgress}
        onDuration={setDuration}
        onEnded={() => {
          if (isSeries) {
            navigateEpisode('next');
          }
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => {
          setPlaying(true);
        }}
      /> */}
      <video
        ref={playerRef}
        src={getSource()}
        controls={true}
        muted={muted}
        style={{
          width: '100vw',
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
        }}
        autoPlay
        onCanPlay={handleReady}
        onWaiting={handleBuffer}
        onPlaying={() => {
          handleBufferEnd();
          setPlaying(true);
        }}
        onError={event => {
          const target = event.target as HTMLVideoElement;
          console.error('Video error:', target.error);
          handlePlayError(target.error);
        }}
        onTimeUpdate={e => {
          const video = e.target as HTMLVideoElement;
          setCurrentTime(video.currentTime);
        }}
        onLoadedMetadata={e => {
          const video = e.target as HTMLVideoElement;
          setDuration(video.duration);
        }}
        onEnded={() => navigateEpisode('next')}
        onPause={() => setPlaying(false)}
      >
        {currentSubtitle && (
          <track
            ref={trackRef}
            kind="subtitles"
            src={currentSubtitle}
            srcLang={selectedSubtitleLanguage || 'en'}
            label={selectedSubtitleLanguage || 'English'}
            default
          />
        )}
      </video>

      {(showControls || !playing || showQualities || showEpisodes) && (
        <div
          dir="rtl"
          className={`absolute bottom-[35px] right-[260px] z-[9999] flex h-[40px] items-center gap-x-10 max-lg:bottom-[28px] max-lg:right-[200px] max-lg:gap-x-4 max-md:right-[140px]`}
          onClick={e => e.stopPropagation()}
        >
          {!trailerSrc && isSeries && (
            <>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  navigateEpisode('next');
                }}
                color="inherit"
              >
                <SkipNext
                  fontSize={window.innerWidth < 1000 ? 'small' : 'large'}
                />
              </IconButton>
              {/* <IconButton
                onClick={e => {
                  e.stopPropagation();
                  navigateEpisode('prev');
                }}
                color="inherit"
              >
                <SkipPrevious
                  fontSize={window.innerWidth < 1000 ? 'small' : 'large'}
                />
              </IconButton> */}
            </>
          )}
          {!trailerSrc && (
            <div className="relative">
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setShowQualities(!showQualities);
                  setShowLanguages(false);
                  setShowSpeed(false);
                  setShowEpisodes(false);
                }}
                color="inherit"
              >
                <Settings
                  fontSize={window.innerWidth < 1000 ? 'small' : 'large'}
                />
              </IconButton>
              {showQualities && (
                <div
                  className="absolute bottom-54 top-auto z-[9999] flex max-h-200 flex-col overflow-y-auto bg-black px-4 py-3"
                  onClick={e => e.stopPropagation()}
                >
                  {Object.keys(
                    isSeries
                      ? (seriesOrMovie as Series)?.seasons?.[
                          currentEpisode?.season ?? ''
                        ]?.episodes?.[currentEpisode?.episode ?? '']
                          ?.qualities || {}
                      : (seriesOrMovie as Movie)?.qualities || {},
                  )
                    ?.filter(qual =>
                      isSeries
                        ? qual
                        : Object.keys(
                            (seriesOrMovie as Movie)?.qualities?.[qual],
                          )?.includes(currentLanguage),
                    )
                    ?.map(q => (
                      <Button
                        key={q}
                        onClick={e => {
                          e.stopPropagation();
                          changeQuality(q); // Use the quality change handler
                          setAnchorEl(null);
                          setShowQualities(false);
                        }}
                        className={`!text-sm ${quality === q ? 'font-bold text-danger' : ''}`}
                      >
                        {q}
                        {/* (
                        {
                          Object.keys(
                            (seriesOrMovie as Movie)?.qualities?.[q] || {},
                          ).length
                        }{' '}
                        <Trans message="Languages" />){q} */}
                      </Button>
                    ))}{' '}
                </div>
              )}
            </div>
          )}
          {!trailerSrc && !isSeries && (
            <div className="relative">
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setShowLanguages(!showLanguages);
                  setShowQualities(false);
                }}
                color="inherit"
              >
                <TranslateIcon
                  fontSize={window.innerWidth < 1000 ? 'small' : 'large'}
                />
              </IconButton>
              {showLanguages && (
                <div className="absolute bottom-54 top-auto z-[9999] flex max-h-200 flex-col overflow-y-auto bg-black px-4 py-3">
                  {/* {Object.keys(
                    (seriesOrMovie as Movie)?.qualities?.[quality] || {},
                  ).map(lang => ( */}
                  {uniqueLanguages?.map(lang => (
                    <Button
                      key={lang}
                      onClick={() => {
                        changeLanguage(lang);
                        setShowLanguages(false);
                      }}
                      className={`!text-sm ${currentLanguage === lang ? 'font-bold text-danger' : ''}`}
                    >
                      <Trans message={lang} />
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!trailerSrc && getAvailableSubtitles().length > 0 && (
            <div className="relative">
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setShowSubtitles(!showSubtitles);
                  setShowLanguages(false);
                  setShowQualities(false);
                  setShowSpeed(false);
                }}
                color="inherit"
              >
                <Subtitles
                  fontSize={window.innerWidth < 1000 ? 'small' : 'large'}
                />
              </IconButton>
              {showSubtitles && (
                <div className="absolute bottom-54 top-auto z-[9999] flex max-h-200 flex-col overflow-y-auto bg-black px-4 py-3">
                  <Button
                    onClick={() => {
                      setCurrentSubtitle(null);
                      setSelectedSubtitleLanguage(null);
                      setShowSubtitles(false);
                    }}
                    className={`!text-sm ${!selectedSubtitleLanguage ? 'font-bold text-danger' : ''}`}
                  >
                    <Trans message="None" />
                  </Button>
                  {getAvailableSubtitles().map(sub => (
                    <Button
                      key={sub.language}
                      onClick={() => handleSubtitleSelect(sub)}
                      className={`!text-sm ${selectedSubtitleLanguage === sub.language ? 'font-bold text-danger' : ''}`}
                    >
                      <Trans message={sub.language} />
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!trailerSrc && isSeries && (
            <div className="relative">
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setShowEpisodes(!showEpisodes);
                  setShowQualities(false);
                  setShowSpeed(false);
                }}
                color="inherit"
              >
                <List fontSize={window.innerWidth < 1000 ? 'small' : 'large'} />
              </IconButton>
              {showEpisodes && (
                <div
                  className="absolute bottom-52 top-auto z-[9999] flex max-h-200 flex-col overflow-y-auto bg-black px-4 py-3"
                  onClick={e => e.stopPropagation()}
                >
                  {Object.entries((seriesOrMovie as Series)?.seasons || {}).map(
                    ([season, seasonData]) => (
                      <Box key={season} sx={{mb: 4}}>
                        <div className="mb-2 cursor-default border-b pb-2">
                          <Trans message={season} />
                        </div>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
                          {Object.keys(seasonData?.episodes || {}).map(
                            episode => (
                              <Button
                                key={episode}
                                onClick={e => {
                                  e.stopPropagation();
                                  setCurrentEpisode({season, episode});
                                  setShowEpisodes(false);
                                  setShowQualities(false);
                                  setShowSpeed(false);
                                }}
                                className={
                                  currentEpisode?.season === season &&
                                  currentEpisode?.episode === episode
                                    ? 'font-bold text-danger'
                                    : ''
                                }
                              >
                                <Trans message={episode} />
                              </Button>
                            ),
                          )}
                        </Box>
                      </Box>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default VideoPlayer;
